import { useStackscanTechs } from "@/hooks/useStackscanTechs";
import type { Technology } from "@/global";
import { HeroImage } from "@/components/shared/HeroImage/HeroImage";
import { CTAButton } from "@/components/shared/CTAButton/CTAButton";
import { PixelBClipDefs } from "@/components/shared/PixelBClipDefs/PixelBClipDefs";
import { TechLogoImage } from "@/components/shared/TechLogoImage/TechLogoImage";
import { Rocket, BookOpen, Lightbulb } from "lucide-react";
import { PageContainer } from "@/routes/Layout/components/PageContainer/PageContainer";
import { useTheme } from "@/contexts/ThemeContext";

export const Home = ({
    technologies = [],
    name,
}: {
    technologies?: Technology[];
    name?: string;
}) => {
    const { theme } = useTheme();
    // Use the hook with a dummy project that includes the stackscanKey
    const stackscanTechs = useStackscanTechs({ stackscanKey: "home" } as any);
    
    // If stackscan hook returns data, use it. Otherwise retain the filtering logic on 'technologies' prop.
    const homepageTechs = (stackscanTechs && stackscanTechs.length > 0)
        ? stackscanTechs
        : technologies.filter(tech => tech.homepage);

    return (
        <>
            <PixelBClipDefs />
            <PageContainer id="home" layout="hero">
                <div className="flex flex-col-reverse md:flex-row items-center gap-8 md:gap-12 w-full">
                    <div className="flex flex-col gap-10 md:gap-12 w-full md:w-2/3 text-left">
                        <h1 className="p-0 m-0 text-3xl md:text-5xl lg:text-6xl text-portfolio-cyan font-satoshi font-bold tracking-wide whitespace-normal md:whitespace-nowrap">
                            <span className="text-slate-700 dark:text-portfolio-white">Hey, I'm</span>
                            <strong className="text-portfolio-green">
                                {` ${name}`}
                            </strong>
                        </h1>
                        <div className="flex flex-col gap-4 text-slate-700 dark:text-portfolio-white text-md md:text-xl font-lunasima">
                            <div className="flex flex-row items-center gap-1.5 flex-wrap">
                                <span>I'm the CTO at</span>
                                <img
                                    src="logos/experience/foxywordmark.svg"
                                    alt="foxy bio"
                                    className="h-5 md:h-6 w-auto object-contain translate-y-0.5 ml-1.5"
                                />
                                <span>, a startup with</span>
                                <a
                                    href="https://sibb.de/en/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center"
                                >
                                    <img
                                        src="logos/experience/SIBB.svg"
                                        alt="SIBB"
                                        className="h-8 md:h-10 w-auto object-contain brightness-0 dark:invert translate-y-1"
                                    />
                                </a>
                            </div>
                            <p>
                                I'm an
                                <img
                                    src={theme === "dark" ? "logos/experience/IronhackDark.png" : "logos/experience/IronhackLight.png"}
                                    alt="Ironhack"
                                    className="inline-block h-5 md:h-6 w-auto mx-1.5 object-contain align-text-bottom"
                                />
                                alum and former assistant teacher
                            </p>
                            <p>I'm focused on modern JavaScript, full-stack development, and clean UX</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-6">
                            {(homepageTechs || []).map((tech, index) => (
                                <TechLogoImage
                                    key={tech.key || index}
                                    image={tech.image}
                                    name={tech.name}
                                    link={tech.link}
                                />
                            ))}
                        </div>
                        <div className="grid grid-cols-2 md:flex md:flex-row gap-4 w-full">
                            <CTAButton
                                onClick={() => window.location.href = '/stack'}
                                text="My stack"
                                icon={Rocket}
                            />
                            <CTAButton
                                onClick={() => window.location.href = '/experience'}
                                text="My story"
                                icon={BookOpen}
                            />
                            <CTAButton
                                onClick={() => window.location.href = '/freelance'}
                                text="My solutions"
                                icon={Lightbulb}
                            />
                        </div>
                    </div>
                    <div className="w-2/4 md:w-1/3 aspect-square">
                        <HeroImage image="/images/heroCropped4.webp" text={name || "Benjamin"} />
                    </div>
                </div>
            </PageContainer>
        </>
    );
};
